import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Bookmark, TrendingUp, TrendingDown, Activity, AlertTriangle,
  DollarSign, BarChart3, ChevronDown, ChevronUp, Target, Zap,
  Shield, Crosshair, Lock, ArrowRight, Loader2, Bell, BellRing,
  Sparkles, Trash2, RefreshCw, CheckCircle2, XCircle, Minus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WATCHLIST_KEY = 'ofs_strategy_watchlist';

const CATEGORY_CONFIG = {
  income: { label: 'Income', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: DollarSign },
  bullish: { label: 'Bullish', color: 'bg-green-100 text-green-800 border-green-300', icon: TrendingUp },
  bearish: { label: 'Bearish', color: 'bg-red-100 text-red-800 border-red-300', icon: TrendingDown },
  neutral: { label: 'Neutral', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Activity },
  volatility: { label: 'Volatility', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: Zap },
  hedging: { label: 'Hedging', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Shield },
  directional: { label: 'Directional', color: 'bg-cyan-100 text-cyan-800 border-cyan-300', icon: Crosshair },
};

const RISK_COLOR = {
  'Low': 'text-green-600',
  'Low-Medium': 'text-lime-600',
  'Medium': 'text-amber-600',
  'High': 'text-red-600',
};

// All 20 strategy definitions (matching backend)
const ALL_STRATEGIES = [
  { id: "covered_call", name: "Covered Call", category: "income", risk_level: "Low", typical_win_rate: 78, avg_return: "2-5% monthly", market_outlook: "Neutral to Mildly Bullish", description: "Sell calls against shares you own to generate premium income.", max_risk: "Stock drops significantly", ideal_iv: "High IV preferred" },
  { id: "cash_secured_put", name: "Cash-Secured Put", category: "income", risk_level: "Low-Medium", typical_win_rate: 75, avg_return: "2-4% monthly", market_outlook: "Neutral to Bullish", description: "Sell puts on stocks you want to own at a lower price.", max_risk: "Assigned shares at strike", ideal_iv: "High IV preferred" },
  { id: "bull_call_spread", name: "Bull Call Spread", category: "bullish", risk_level: "Medium", typical_win_rate: 45, avg_return: "50-200%", market_outlook: "Moderately Bullish", description: "Buy a call and sell a higher-strike call for limited risk upside.", max_risk: "Net debit paid", ideal_iv: "Low-moderate IV" },
  { id: "bear_put_spread", name: "Bear Put Spread", category: "bearish", risk_level: "Medium", typical_win_rate: 45, avg_return: "50-200%", market_outlook: "Moderately Bearish", description: "Buy a put and sell a lower-strike put for limited risk downside.", max_risk: "Net debit paid", ideal_iv: "Low-moderate IV" },
  { id: "iron_condor", name: "Iron Condor", category: "neutral", risk_level: "Medium", typical_win_rate: 65, avg_return: "10-30%", market_outlook: "Neutral / Range-bound", description: "Sell OTM put spread and call spread. Profits in a range.", max_risk: "Spread width minus credit", ideal_iv: "High IV" },
  { id: "iron_butterfly", name: "Iron Butterfly", category: "neutral", risk_level: "Medium", typical_win_rate: 40, avg_return: "20-50%", market_outlook: "Neutral / Low Vol Expected", description: "Sell ATM straddle with OTM wings for protection.", max_risk: "Spread width minus credit", ideal_iv: "High IV, expecting crush" },
  { id: "long_straddle", name: "Long Straddle", category: "volatility", risk_level: "High", typical_win_rate: 35, avg_return: "100%+", market_outlook: "Expecting High Volatility", description: "Buy ATM call and put. Profits from large moves either direction.", max_risk: "Total premium paid", ideal_iv: "Low IV (buy cheap)" },
  { id: "long_strangle", name: "Long Strangle", category: "volatility", risk_level: "High", typical_win_rate: 30, avg_return: "150%+", market_outlook: "Expecting High Volatility", description: "Buy OTM call and put. Cheaper than straddle, needs bigger move.", max_risk: "Total premium paid", ideal_iv: "Low IV" },
  { id: "calendar_spread", name: "Calendar Spread", category: "neutral", risk_level: "Medium", typical_win_rate: 55, avg_return: "15-40%", market_outlook: "Neutral short-term", description: "Sell near-term, buy longer-term at same strike. Time decay play.", max_risk: "Net debit paid", ideal_iv: "Low near-term IV" },
  { id: "diagonal_spread", name: "Diagonal Spread", category: "directional", risk_level: "Medium", typical_win_rate: 50, avg_return: "20-60%", market_outlook: "Mildly Directional", description: "Calendar + vertical spread combo. Different strikes and expirations.", max_risk: "Net debit paid", ideal_iv: "Moderate IV" },
  { id: "butterfly_spread", name: "Butterfly Spread", category: "neutral", risk_level: "Low-Medium", typical_win_rate: 30, avg_return: "200-500%", market_outlook: "Pin to specific price", description: "Buy 1 ITM, sell 2 ATM, buy 1 OTM. High reward if stock pins.", max_risk: "Very small debit", ideal_iv: "High IV preferred" },
  { id: "collar", name: "Collar", category: "hedging", risk_level: "Low", typical_win_rate: 85, avg_return: "Limited (protection)", market_outlook: "Protecting gains", description: "Own shares + buy put + sell call. Locks in a profit range.", max_risk: "Limited to collar range", ideal_iv: "Any" },
  { id: "protective_put", name: "Protective Put", category: "hedging", risk_level: "Low", typical_win_rate: 90, avg_return: "Protection strategy", market_outlook: "Bullish but cautious", description: "Buy a put to protect shares. Insurance against downside.", max_risk: "Premium paid", ideal_iv: "Low IV (cheap insurance)" },
  { id: "bull_put_spread", name: "Bull Put Spread (Credit)", category: "income", risk_level: "Medium", typical_win_rate: 68, avg_return: "10-30%", market_outlook: "Neutral to Bullish", description: "Sell OTM put, buy further OTM put. Collect credit.", max_risk: "Spread width minus credit", ideal_iv: "High IV preferred" },
  { id: "bear_call_spread", name: "Bear Call Spread (Credit)", category: "income", risk_level: "Medium", typical_win_rate: 68, avg_return: "10-30%", market_outlook: "Neutral to Bearish", description: "Sell OTM call, buy further OTM call. Collect credit.", max_risk: "Spread width minus credit", ideal_iv: "High IV preferred" },
  { id: "jade_lizard", name: "Jade Lizard", category: "income", risk_level: "Medium", typical_win_rate: 70, avg_return: "15-30%", market_outlook: "Neutral to Bullish", description: "Sell put + sell call spread. No upside risk if structured right.", max_risk: "Downside like short put", ideal_iv: "High IV" },
  { id: "ratio_spread", name: "Ratio Spread", category: "directional", risk_level: "High", typical_win_rate: 55, avg_return: "30-100%+", market_outlook: "Moderately Directional", description: "Buy 1, sell 2 at further strike. Can enter for credit.", max_risk: "Unlimited on naked side", ideal_iv: "High IV" },
  { id: "pmcc", name: "Poor Man's Covered Call", category: "bullish", risk_level: "Medium", typical_win_rate: 65, avg_return: "3-8% monthly", market_outlook: "Moderately Bullish", description: "Buy deep ITM LEAPS, sell short-term OTM calls against it.", max_risk: "LEAPS debit", ideal_iv: "Low for LEAPS, high for calls" },
  { id: "short_straddle", name: "Short Straddle", category: "income", risk_level: "High", typical_win_rate: 55, avg_return: "20-50%", market_outlook: "Strongly Neutral", description: "Sell ATM call and put. Max premium but unlimited risk.", max_risk: "Unlimited (needs margin)", ideal_iv: "Very High IV" },
  { id: "credit_spread_combo", name: "Double Credit Spread", category: "income", risk_level: "Medium", typical_win_rate: 62, avg_return: "15-35%", market_outlook: "Neutral / Range-bound", description: "Sell bull put + bear call spread simultaneously.", max_risk: "Wider spread minus credit", ideal_iv: "High IV preferred" },
];

const getWatchlist = () => {
  try {
    const stored = localStorage.getItem(WATCHLIST_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveWatchlist = (ids) => {
  localStorage.setItem(WATCHLIST_KEY, JSON.stringify(ids));
};

const StrategyWatchlist = () => {
  const [watchlistIds, setWatchlistIds] = useState(getWatchlist);
  const [conditions, setConditions] = useState(null);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  const fetchConditions = useCallback(async () => {
    if (watchlistIds.length === 0) return;
    setLoadingConditions(true);
    try {
      const res = await axios.get(`${API}/market-conditions/current`, { timeout: 30000 });
      setConditions(res.data);
    } catch (err) {
      console.error('Failed to fetch market conditions:', err);
      toast.error('Failed to load market conditions');
    } finally {
      setLoadingConditions(false);
    }
  }, [watchlistIds.length]);

  useEffect(() => {
    fetchConditions();
  }, [fetchConditions]);

  // Listen for watchlist changes from Scanner tab
  useEffect(() => {
    const handler = () => {
      setWatchlistIds(getWatchlist());
    };
    window.addEventListener('watchlist-updated', handler);
    return () => window.removeEventListener('watchlist-updated', handler);
  }, []);

  const removeFromWatchlist = (stratId) => {
    const updated = watchlistIds.filter(id => id !== stratId);
    setWatchlistIds(updated);
    saveWatchlist(updated);
    window.dispatchEvent(new Event('watchlist-updated'));
    toast.success('Removed from watchlist');
  };

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const watchedStrategies = ALL_STRATEGIES.filter(s => watchlistIds.includes(s.id));
  const stratConditions = conditions?.strategy_conditions || {};

  const alertStrategies = watchedStrategies.filter(s => stratConditions[s.id] === true);
  const otherStrategies = watchedStrategies.filter(s => stratConditions[s.id] !== true);

  return (
    <div className="space-y-6" data-testid="strategy-watchlist">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-2">
                <div className="p-2 bg-indigo-500/20 rounded-lg">
                  <Bookmark className="h-6 w-6 text-indigo-400" />
                </div>
                Strategy Watchlist
              </CardTitle>
              <p className="text-slate-400 mt-1 text-sm">
                {watchlistIds.length} saved strategies with market condition alerts
              </p>
            </div>
            <div className="flex items-center gap-3">
              {conditions && (
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    VIX: {conditions.vix}
                  </Badge>
                  <Badge variant="outline" className={`border-slate-600 ${
                    conditions.market_regime === 'bullish' ? 'text-green-400' :
                    conditions.market_regime === 'bearish' ? 'text-red-400' : 'text-blue-400'
                  }`}>
                    Market: {conditions.market_regime}
                  </Badge>
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    IV: {conditions.iv_environment}
                  </Badge>
                </div>
              )}
              <Button
                onClick={fetchConditions}
                disabled={loadingConditions}
                variant="outline"
                size="icon"
                className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
                data-testid="refresh-conditions-btn"
              >
                <RefreshCw className={`h-4 w-4 ${loadingConditions ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Empty State */}
      {watchlistIds.length === 0 && (
        <Card className="border-dashed border-2 border-slate-300">
          <CardContent className="py-16 text-center">
            <Bookmark className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-600">Your watchlist is empty</p>
            <p className="text-sm text-slate-400 mt-1">
              Go to the <strong>Scanner</strong> tab and click the bookmark icon on any strategy to add it here.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Alerts Section */}
      {alertStrategies.length > 0 && (
        <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-green-800">
              <BellRing className="h-5 w-5 text-green-600 animate-pulse" />
              Favorable Conditions Detected
              <Badge className="bg-green-600 text-white ml-2">{alertStrategies.length}</Badge>
            </CardTitle>
            <p className="text-xs text-green-700">
              These watchlisted strategies align with current market conditions
              {conditions && ` (VIX: ${conditions.vix}, Market: ${conditions.market_regime}, IV: ${conditions.iv_environment})`}
            </p>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {alertStrategies.map(strat => {
                const catCfg = CATEGORY_CONFIG[strat.category] || CATEGORY_CONFIG.neutral;
                const CatIcon = catCfg.icon;
                return (
                  <div
                    key={strat.id}
                    className="bg-white rounded-lg p-4 border border-green-200 shadow-sm flex items-start gap-3"
                    data-testid={`alert-${strat.id}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900 text-sm">{strat.name}</span>
                        <Badge className={`text-xs border ${catCfg.color}`} variant="outline">
                          <CatIcon className="h-3 w-3 mr-1" />{catCfg.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{strat.description}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs">
                        <span className={`font-semibold ${RISK_COLOR[strat.risk_level] || ''}`}>
                          {strat.risk_level} Risk
                        </span>
                        <span className="text-indigo-600 font-semibold">{strat.typical_win_rate}% Win</span>
                        <span className="text-emerald-600">{strat.avg_return}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-slate-400 hover:text-red-500"
                      onClick={() => removeFromWatchlist(strat.id)}
                      data-testid={`remove-alert-${strat.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Other Watchlist Items */}
      {otherStrategies.length > 0 && (
        <>
          {alertStrategies.length > 0 && (
            <p className="text-sm text-slate-500 font-medium flex items-center gap-2">
              <Minus className="h-4 w-4" />
              Waiting for favorable conditions ({otherStrategies.length})
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherStrategies.map(strat => {
              const catCfg = CATEGORY_CONFIG[strat.category] || CATEGORY_CONFIG.neutral;
              const CatIcon = catCfg.icon;
              const isExpanded = expandedCards[strat.id];
              const condMet = stratConditions[strat.id];

              return (
                <Card
                  key={strat.id}
                  className="border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all"
                  data-testid={`watchlist-card-${strat.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-base font-bold text-slate-900">{strat.name}</span>
                          <Badge className={`text-xs border ${catCfg.color}`} variant="outline">
                            <CatIcon className="h-3 w-3 mr-1" />{catCfg.label}
                          </Badge>
                          {condMet === false && (
                            <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-500">
                              <XCircle className="h-3 w-3 mr-1" />Conditions Not Met
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{strat.description}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpand(strat.id)}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-slate-400 hover:text-red-500"
                          onClick={() => removeFromWatchlist(strat.id)}
                          data-testid={`remove-${strat.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className={`font-semibold ${RISK_COLOR[strat.risk_level] || ''}`}>
                        <AlertTriangle className="h-3 w-3 inline mr-0.5" />{strat.risk_level} Risk
                      </span>
                      <span className="text-slate-500">|</span>
                      <span className="text-indigo-600 font-semibold">
                        <BarChart3 className="h-3 w-3 inline mr-0.5" />{strat.typical_win_rate}% Win Rate
                      </span>
                      <span className="text-slate-500">|</span>
                      <span className="text-emerald-600">{strat.avg_return}</span>
                    </div>
                  </CardHeader>

                  {isExpanded && (
                    <CardContent className="pt-0">
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-slate-400">Outlook:</span>
                          <span className="ml-1 text-slate-700 font-medium">{strat.market_outlook}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Max Risk:</span>
                          <span className="ml-1 text-slate-700">{strat.max_risk}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Ideal IV:</span>
                          <span className="ml-1 text-slate-700">{strat.ideal_iv}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Win Rate:</span>
                          <span className="ml-1 text-indigo-600 font-semibold">{strat.typical_win_rate}%</span>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Loading conditions overlay */}
      {loadingConditions && watchlistIds.length > 0 && !conditions && (
        <div className="text-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500 mb-2" />
          <p className="text-sm text-slate-500">Loading market conditions...</p>
        </div>
      )}
    </div>
  );
};

export default StrategyWatchlist;
